import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { updateFieldsFromSavedSearch } from '../../adapters/book-widget-fields-saved-search-update/book-widget-fields-saved-search-update.adapter';
import { formatErrorCode } from '../../adapters/utils/utils.adapter';
import { BOOK_WIDGET_ERROR_CODES } from '../../constants/error-codes.constants';
import { SavedSearchResponse } from '../../models/core-apis/saved-search.interfaces';
import { SavedSearchService } from '../../services/saved-search.service';
import * as fromBookWidgetActions from '../actions/book-widget.actions';
import * as fromErrorHandlerActions from '../actions/error-handler.actions';
import * as fromSavedSearchAction from '../actions/saved-search.actions';
import {
  getBookWidgetFields,
  getBookWidgetFieldsMasterConfig,
  getFlightShopTemplateType
} from '../selectors/book-widget.selectors';
import { isLogin } from '../selectors/login-dashboard.selectors';
import {
  getSavedSearchDataList,
  isSavedSearchDataErrored,
  isSavedSearchDataReady
} from '../selectors/saved-search.selectors';

@Injectable({ providedIn: 'root' })
export class SavedSearchEffects {
  loadSavedSearch$ = createEffect(
    (): Observable<Action> =>
      this.SavedSearchActions$.pipe(
        ofType(fromSavedSearchAction.LoadSavedSearch),
        concatLatestFrom(() => [
          this.store.select(isLogin),
          this.store.select(isSavedSearchDataReady),
          this.store.select(isSavedSearchDataErrored),
          this.store.select(getFlightShopTemplateType)
        ]),
        filter(
          ([, isLogin, isReady, isError]) => isLogin && (!isReady || isError)
        ),
        switchMap(([, , , , flightShopWidgetTemplate]) =>
          this.service.getSavedSearch(flightShopWidgetTemplate).pipe(
            map((savedSearchResponse: SavedSearchResponse) =>
              fromSavedSearchAction.LoadSavedSearchSuccess({
                savedSearchResponse
              })
            ),
            catchError((error: HttpErrorResponse) => {
              return of(
                fromErrorHandlerActions.HandleError({
                  errorCode: formatErrorCode(
                    BOOK_WIDGET_ERROR_CODES.SAVED_SEARCH_FAILURE,
                    error?.status
                  ),
                  error: BOOK_WIDGET_ERROR_CODES.SAVED_SEARCH_FAILURE
                }),
                fromSavedSearchAction.LoadSavedSearchFailure()
              );
            })
          )
        )
      )
  );

  savedSearchItemSelected$ = createEffect(
    (): Observable<Action> =>
      this.SavedSearchActions$.pipe(
        ofType(fromBookWidgetActions.SavedSearchItemSelected),
        concatLatestFrom(() => [
          this.store.select(getSavedSearchDataList),
          this.store.select(getBookWidgetFields),
          this.store.select(getBookWidgetFieldsMasterConfig)
        ]),
        switchMap(
          ([
            { index },
            savedSearchDataList,
            bookWidgetFields,
            bookWidgetFieldsMasterConfig
          ]) => {
            const updatedBookWidgetFields = updateFieldsFromSavedSearch(
              savedSearchDataList || null,
              index,
              bookWidgetFields,
              bookWidgetFieldsMasterConfig
            );
            return of(
              fromBookWidgetActions.UpdateFieldsFromSavedSearch({
                bookWidgetFields: updatedBookWidgetFields
              })
            );
          }
        )
      )
  );

  constructor(
    private SavedSearchActions$: Actions,
    private service: SavedSearchService,
    private store: Store
  ) {}
}
